import { useState } from 'react';
import { Hash, Lock } from 'lucide-react';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { BACKEND_URL } from '@/config/api';

export const CreateChannelModal = ({ 
  isOpen, 
  onClose, 
  token, 
  selectedHub, 
  onChannelCreated,
  onNavigate 
}) => {
  const [channelName, setChannelName] = useState('');
  const [channelDesc, setChannelDesc] = useState('');
  const [isPrivate, setIsPrivate] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleCreate = async () => {
    if (!channelName.trim()) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(`${BACKEND_URL}/api/channels`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          name: channelName.toLowerCase().replace(/\s+/g, '-'),
          description: channelDesc,
          is_private: isPrivate,
          hub_id: selectedHub?.id !== 'default' ? selectedHub?.id : null
        })
      });
      
      if (response.ok) {
        const newChannel = await response.json();
        onChannelCreated?.(newChannel);
        onNavigate?.('/messages');
        handleClose();
      }
    } catch (error) {
      console.error('Error creating channel:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setChannelName('');
    setChannelDesc('');
    setIsPrivate(false);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-[#0F2942] border-white/20 text-white">
        <DialogHeader>
          <DialogTitle>Create Channel</DialogTitle>
          <DialogDescription className="text-white/60">
            Channels are where conversations happen around a topic.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-white/80">Name</Label>
            <div className="relative">
              <Hash className="absolute left-3 top-1/2 -translate-y-1/2 text-white/40" size={16} />
              <Input
                id="name"
                value={channelName}
                onChange={(e) => setChannelName(e.target.value.toLowerCase().replace(/\s+/g, '-'))}
                placeholder="channel-name"
                className="pl-9 bg-white/10 border-white/20 text-white placeholder:text-white/40"
                data-testid="channel-name-input"
              />
            </div>
            <p className="text-xs text-white/40">Channel names are lowercase and use dashes instead of spaces.</p>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="description" className="text-white/80">Description <span className="text-white/40">(optional)</span></Label>
            <Input
              id="description"
              value={channelDesc}
              onChange={(e) => setChannelDesc(e.target.value)}
              placeholder="What's this channel about?"
              className="bg-white/10 border-white/20 text-white placeholder:text-white/40"
            />
          </div>
          
          <div className="flex items-center justify-between py-2">
            <div>
              <Label htmlFor="private" className="text-white/80 flex items-center gap-2">
                <Lock size={16} className="text-white/60" />
                Make private
              </Label>
              <p className="text-xs text-white/40 ml-6">
                {isPrivate ? 'Only invited members can see this channel' : 'Anyone in the hub can see this channel'}
              </p>
            </div>
            <Switch
              id="private"
              checked={isPrivate}
              onCheckedChange={setIsPrivate}
            />
          </div>
        </div>
        
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={handleClose}
            className="text-white/60 hover:text-white hover:bg-white/10"
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={!channelName.trim() || isLoading}
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
            data-testid="create-channel-submit"
          >
            {isLoading ? 'Creating...' : 'Create Channel'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
